import constants from '@/config/constants';
import store from '../stores/store';

import AsyncStorage from '@react-native-async-storage/async-storage';
import { logError } from './logHandling';

type VersionInfo = {
  minimumVersion: string;
  latestVersion: string;
};

type VersionCompareOptions = {
  lexicographical: boolean;
  zeroExtend: boolean;
};

const appVersion = constants.appVersion ?? '';

export async function compareVersions(versionInfo: VersionInfo) {
  let minVersion = versionInfo.minimumVersion;
  let latestVersion = versionInfo.latestVersion;

  let comparedToMinVersion = versionCompare(appVersion, minVersion);
  let comparedToLatestVersion = versionCompare(appVersion, latestVersion);

  let seenUpdates: Record<string, unknown> = {};
  // Read seenUpdates from AsyncStorage
  try {
    const seenUpdatesString = await AsyncStorage.getItem('seenUpdates');
    seenUpdates = JSON.parse(seenUpdatesString || '{}');
  } catch (error) {
    logError('Error reading seenUpdates from AsyncStorage:', error);
  }

  if (comparedToMinVersion < 0) {
    store.getActions().setShowCriticalAppUpdateModal(true);
  } else if (comparedToLatestVersion < 0 && !seenUpdates[latestVersion]) {
    store.getActions().setShowAppNeedToBeUpdatedModal(true);

    // Update seenUpdates with new information
    const updatedSeenUpdates = {
      ...seenUpdates,
      [latestVersion]: true, // Mark the latest version as seen
    };
    try {
      await AsyncStorage.setItem(
        'seenUpdates',
        JSON.stringify(updatedSeenUpdates),
      );
    } catch (error) {
      logError('Error saving updated seenUpdates to AsyncStorage:', error);
    }
  }
}

// compares semantic versions: returns negative int if version is smaller, 0 if is equal, and positive if bigger
function versionCompare(
  v1: string,
  v2: string,
  options?: VersionCompareOptions,
) {
  let lexicographical = options && options.lexicographical,
    zeroExtend = options && options.zeroExtend,
    v1parts: any[] = v1.split('.'),
    v2parts: any[] = v2.split('.');

  function isValidPart(x: string) {
    return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
  }

  if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
    return NaN;
  }

  if (zeroExtend) {
    while (v1parts.length < v2parts.length) v1parts.push('0');
    while (v2parts.length < v1parts.length) v2parts.push('0');
  }

  if (!lexicographical) {
    v1parts = v1parts.map(Number);
    v2parts = v2parts.map(Number);
  }

  for (let i = 0; i < v1parts.length; ++i) {
    if (v2parts.length == i) {
      return 1;
    }

    if (v1parts[i] == v2parts[i]) {
      continue;
    } else if (v1parts[i] > v2parts[i]) {
      return 1;
    } else {
      return -1;
    }
  }

  if (v1parts.length != v2parts.length) {
    return -1;
  }

  return 0;
}
